import { useEffect, useRef, useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";

const STORE_KEY = "towhook.settings.open";

const readOpen = () => {
  try {
    return JSON.parse(localStorage.getItem(STORE_KEY) || "{}");
  } catch {
    return {};
  }
};

/**
 * One foldable slot on the Integrations page.
 *
 * Closed it's a single clickable row, which is the point — the page was a
 * mile of open forms. Open, the card renders exactly as it always has and
 * the row shrinks to a plain "Hide" strip, so the card's own heading stays
 * the only title on screen. Which ones are open is remembered per browser.
 */
export default function SettingsFold({ id, title, description, icon: Icon, tone, children }) {
  // `#storage-rates` etc. opens that fold and scrolls to it. Onboarding's
  // "Customise in Settings" links used to drop people on a page of closed
  // cards and leave them hunting for the one they had just been reading.
  const [open, setOpen] = useState(
    () => window.location.hash.slice(1) === id || !!readOpen()[id],
  );
  const ref = useRef(null);

  useEffect(() => {
    const all = readOpen();
    if (open) all[id] = true; else delete all[id];
    try {
      localStorage.setItem(STORE_KEY, JSON.stringify(all));
    } catch { /* private browsing — folds just won't be remembered */ }
  }, [id, open]);

  useEffect(() => {
    const jump = () => {
      if (window.location.hash.slice(1) !== id) return;
      setOpen(true);
      // Deferred twice: the fold has to re-render open before the browser
      // can scroll to where it ends up.
      requestAnimationFrame(() =>
        requestAnimationFrame(() =>
          ref.current?.scrollIntoView({ behavior: "smooth", block: "start" }),
        ),
      );
    };
    jump();
    // A second click on the same link fires hashchange, not a remount.
    window.addEventListener("hashchange", jump);
    return () => window.removeEventListener("hashchange", jump);
  }, [id]);

  const danger = tone === "danger";

  if (open) {
    return (
      <section data-testid={`fold-${id}`} data-open="true" ref={ref} id={id}>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="w-full flex items-center gap-1.5 px-1 pb-2 text-xs font-semibold text-slate-500 hover:text-slate-800 dark:hover:text-slate-200 transition-colors"
          data-testid={`fold-toggle-${id}`}
        >
          <ChevronUp className="h-3.5 w-3.5" /> Hide
        </button>
        {children}
      </section>
    );
  }

  return (
    <section
      className={`rounded-md border bg-white dark:bg-slate-900 transition-colors ${
        danger
          ? "border-rose-300 dark:border-rose-800 hover:border-rose-500"
          : "border-slate-200 dark:border-slate-700 hover:border-slate-400 dark:hover:border-slate-500"
      }`}
      data-testid={`fold-${id}`}
      data-open="false"
      ref={ref}
      id={id}
    >
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="w-full flex items-center gap-3 px-4 py-3.5 text-left"
        data-testid={`fold-toggle-${id}`}
      >
        {Icon && (
          <Icon className={`h-5 w-5 shrink-0 ${danger ? "text-rose-600" : "text-blue-600"}`} />
        )}
        <span className="min-w-0 flex-1">
          <span
            className={`block font-display font-bold ${
              danger ? "text-rose-700 dark:text-rose-300" : "text-slate-800 dark:text-slate-100"
            }`}
          >
            {title}
          </span>
          {description && (
            <span className="block text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              {description}
            </span>
          )}
        </span>
        <ChevronDown className="h-4 w-4 shrink-0 text-slate-400" />
      </button>
    </section>
  );
}
